import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { MEETING_DAYS } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { groupsApi } from '@/services/groups.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

/**
 * Creates a group.
 *
 * Only a name is required. A leader setting up a Tuesday study often does not
 * yet know the room or the cap, and demanding them produces placeholder values
 * that are worse than an honest blank.
 */
export function GroupForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    capacity: '',
    meetingDay: '',
    meetingTime: '',
    meetingLocation: '',
  });

  useEffect(() => {
    // `showModal` rather than `show`: it traps focus, renders the backdrop and
    // makes Escape close the dialog, all without a focus-trap library.
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: () =>
      groupsApi.create({
        name: form.name.trim(),
        description: form.description.trim() || null,
        // An empty input is "no limit", not zero. The API rejects zero.
        capacity: form.capacity ? Number(form.capacity) : null,
        meetingDay: form.meetingDay ? (form.meetingDay as (typeof MEETING_DAYS)[number]) : null,
        meetingTime: form.meetingTime || null,
        meetingLocation: form.meetingLocation.trim() || null,
      }),
    onSuccess: onCreated,
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        const fields = error.fieldErrors;
        setFieldErrors(fields);
        if (Object.keys(fields).length === 0) {
          setFormError(t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') }));
        }
        return;
      }
      setFormError(t('errors.INTERNAL_ERROR'));
    },
  });

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);
    mutation.mutate();
  }

  return (
    <dialog ref={dialogRef} onClose={onClose} className="dialog" aria-labelledby="group-form-title">
      <form onSubmit={handleSubmit} noValidate className="dialog-body">
        <h2 id="group-form-title">{t('groups.group.create')}</h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        <Field
          label={t('groups.group.name')}
          name="name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          error={fieldErrors.name}
          required
          autoFocus
        />

        <Field
          label={t('groups.group.description')}
          name="description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          error={fieldErrors.description}
        />

        <div className="form-grid">
          <div className="field">
            <label className="field-label" htmlFor="field-meetingDay">
              {t('groups.group.meetingDay')}
            </label>
            <select
              id="field-meetingDay"
              name="meetingDay"
              className="field-input"
              value={form.meetingDay}
              onChange={(e) => setForm({ ...form, meetingDay: e.target.value })}
            >
              <option value="">—</option>
              {MEETING_DAYS.map((day) => (
                <option key={day} value={day}>
                  {t(`groups.day.${day}`)}
                </option>
              ))}
            </select>
          </div>

          <Field
            label={t('groups.group.meetingTime')}
            name="meetingTime"
            type="time"
            value={form.meetingTime}
            onChange={(e) => setForm({ ...form, meetingTime: e.target.value })}
            error={fieldErrors.meetingTime}
            dir="ltr"
          />
        </div>

        <div className="form-grid">
          <Field
            label={t('groups.group.meetingLocation')}
            name="meetingLocation"
            value={form.meetingLocation}
            onChange={(e) => setForm({ ...form, meetingLocation: e.target.value })}
            error={fieldErrors.meetingLocation}
          />

          <Field
            label={t('groups.group.capacity')}
            hint={t('groups.group.capacityHint')}
            name="capacity"
            type="number"
            min={1}
            value={form.capacity}
            onChange={(e) => setForm({ ...form, capacity: e.target.value })}
            error={fieldErrors.capacity}
            dir="ltr"
          />
        </div>

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            {t('common.actions.save')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
